import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import WavyBackground from '../components/WavyBackground';
import apiService from '../services/api';
import '../styles/CarDetail.css';

const CarDetail = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fromStock = location.state?.from || `/stock${location.search}`;

  useEffect(() => {
    const fetchCar = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getCarById(id);

        if (response.success) {
          setCar(response.data);
        } else {
          setError('Véhicule non trouvé');
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        setError('Erreur lors du chargement du véhicule');
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id]);


  if (loading) {
    return (
      <div className="container">
        <div className="loading-message">
          <h2>Chargement du véhicule...</h2>
        </div>
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="container">
        <div className="car-not-found">
          <h2>Véhicule non trouvé</h2>
          <p>{error || 'Le véhicule que vous recherchez n\'existe pas ou a été supprimé.'}</p>
          <div className="error-actions">
            <Link to={fromStock} className="btn btn-primary">
              Retour à la liste
            </Link>
            <button onClick={() => window.location.reload()} className="btn btn-outline">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatKilometrage = (km) => {
    return new Intl.NumberFormat('fr-FR').format(km) + ' km';
  };

  const getEnergyIcon = (energie) => {
    switch (energie.toLowerCase()) {
      case 'essence':
        return '⛽';
      case 'diesel':
        return '🛢️';
      case 'électrique':
        return '⚡';
      case 'hybride':
        return '⚡⛽';
      case 'hybride rechargeable':
        return '🔌';
      default:
        return '⛽';
    }
  };

  const nextImage = () => {
    if (isNavigating) return; // Empêcher les clics multiples rapides
    setIsNavigating(true);

    setCurrentImageIndex((prev) =>
      prev === car.images.length - 1 ? 0 : prev + 1
    );

    setTimeout(() => setIsNavigating(false), 300); // Délai de 300ms
  };

  const prevImage = () => {
    if (isNavigating) return; // Empêcher les clics multiples rapides
    setIsNavigating(true);

    setCurrentImageIndex((prev) =>
      prev === 0 ? car.images.length - 1 : prev - 1
    );

    setTimeout(() => setIsNavigating(false), 300); // Délai de 300ms
  };

  return (
    <div className="detail-page">
      {/* Section 1 : En-tête */}
      <section className="detail-header-section">
        <WavyBackground
          backgroundFill="#1a1a1a"
          colors={["#8f8e85", "#a8a26c", "#ccbe45"]}
          waveWidth={30}
          blur={0.9}
          speed="medium"
          waveOpacity={0.15}
          containerClassName="detail-header-wavy"
        />
        <div className="container">
          <div className="detail-breadcrumb">
            <Link to="/">Accueil</Link> /
            <Link to={fromStock}>Véhicules</Link> /
            <span>{car.marque} {car.modele}</span>
          </div>

          <div className="detail-header-banner">
            <h1>{car.marque} {car.modele}</h1>
            <div className="detail-price-banner">
              <span className="amount">{formatPrice(car.prix)}</span>
              {car.disponible ? (
                <span className="detail-status available">Disponible</span>
              ) : (
                <span className="detail-status sold">Vendu</span>
              )}
            </div>

            <div className="detail-header-actions">
              {car.disponible ? (
                <>
                  {/* Version Desktop */}
                  <a
                    href="mailto:fdp-automobiles@live.fr?subject=Demande d'information - {car.marque} {car.modele} ({car.vo})"
                    className="btn btn-primary detail-btn-large desktop-only-btn"
                  >
                    Nous contacter
                  </a>

                  {/* Version Mobile */}
                  <div className="mobile-contact-section">
                    <p className="mobile-contact-label">Nous contacter :</p>
                    <div className="mobile-contact-buttons">
                      <a
                        href="mailto:fdp-automobiles@live.fr?subject=Demande d'information - {car.marque} {car.modele} ({car.vo})"
                        className="btn btn-primary detail-btn-compact"
                      >
                        Par mail
                      </a>
                      <a
                        href="tel:0247540186"
                        className="btn btn-primary detail-btn-compact"
                      >
                        Par téléphone
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <button className="btn btn-disabled detail-btn-large" disabled>
                  Véhicule vendu
                </button>
              )}

              <button
                onClick={() => {
                  // Si possible, revenir en arrière dans l'historique pour conserver exactement l'état
                  if (location.state?.from) {
                    navigate(location.state.from);
                  } else if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    // Fallback: reconstruire l'URL depuis sessionStorage si disponible
                    let targetSearch = location.search || '';
                    try {
                      const saved = sessionStorage.getItem('carListState');
                      if (saved) {
                        const { filters, page } = JSON.parse(saved);
                        const params = new URLSearchParams();
                        if (filters) {
                          Object.keys(filters).forEach(k => {
                            if (filters[k]) params.set(k, filters[k]);
                          });
                        }
                        if (page && page !== 1) params.set('page', page);
                        targetSearch = params.toString() ? `?${params.toString()}` : '';
                      }
                    } catch (e) {
                      // ignore
                    }

                    navigate(`/stock${targetSearch}`);
                  }
                }}
                className="btn btn-outline detail-btn-large"
              >
                ← Retour à la liste
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 : Images */}
      <section className="detail-images-section">
        <div className="container">
          <div className="detail-images">
            <div className="detail-main-image">
              <img
                src={car.images[currentImageIndex] || '/img/defaultImgCar.webp'}
                alt={`${car.marque} ${car.modele}`}
                onError={(e) => {
                  e.target.src = '/img/defaultImgCar.webp';
                }}
              />
              {car.images.length > 1 && (
                <>
                  <button className="detail-nav-btn prev" onClick={prevImage}>
                    &#8249;
                  </button>
                  <button className="detail-nav-btn next" onClick={nextImage}>
                    &#8250;
                  </button>
                </>
              )}
              {!car.disponible && <div className="detail-sold-overlay">VENDU</div>}
            </div>

            {car.images.length > 1 && (
              <div className="detail-thumbnails">
                {car.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${car.marque} ${car.modele} - ${index + 1}`}
                    className={index === currentImageIndex ? 'active' : ''}
                    onClick={() => setCurrentImageIndex(index)}
                    onError={(e) => {
                      e.target.src = '/img/defaultImgCar.webp';
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section 3 : Fiche technique */}
      <section className="detail-specs-section">
        <div className="container">
          <div className="detail-specs">
            <h3>Fiche technique</h3>
            <div className="detail-specs-grid">
              <div className="detail-spec-item">
                <strong>MARQUE</strong>
                <span>{car.marque}</span>
              </div>
              <div className="detail-spec-item">
                <strong>MODÈLE</strong>
                <span>{car.modele}</span>
              </div>
              <div className="detail-spec-item">
                <strong>ANNÉE</strong>
                <span>{car.annee}</span>
              </div>
              <div className="detail-spec-item">
                <strong>KILOMÉTRAGE</strong>
                <span>{formatKilometrage(car.kilometrage)}</span>
              </div>
              <div className="detail-spec-item">
                <strong>CARBURANT</strong>
                <span>{getEnergyIcon(car.energie)} {car.energie}</span>
              </div>
              <div className="detail-spec-item">
                <strong>TRANSMISSION</strong>
                <span>{car.transmission}</span>
              </div>
              <div className="detail-spec-item">
                <strong>PUISS. FISCALE</strong>
                <span>{car.puissanceFiscale} CV</span>
              </div>
              <div className="detail-spec-item">
                <strong>COULEUR</strong>
                <span>{car.couleur}</span>
              </div>
              <div className="detail-spec-item">
                <strong>DATE D'IMMATRICULATION</strong>
                <span>{new Date(car.dateImmatriculation).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="detail-spec-item">
                <strong>NUMÉRO VO</strong>
                <span>{car.vo}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 : Description */}
      <section className="detail-description-section">
        <div className="container">
          <div className="detail-description">
            <h3>Description</h3>
            <p>{car.description}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarDetail;
